import type { AnyMicaraHrEvent, EventName, EventPayloadMap, MicaraHrEvent } from './events';

export type EventHandler<TName extends EventName> = (event: MicaraHrEvent<TName>) => void;
export type AnyEventHandler = (event: AnyMicaraHrEvent) => void;
export type Unsubscribe = () => void;

/**
 * Transport-agnostic realtime interface.
 *
 * The mock implementation is an in-memory emitter. When the backend arrives,
 * a WebSocket (or SSE / Ably / Pusher) client implements the same interface and
 * nothing in the applications changes.
 *
 * TODO(production): replace `MockEventBus` with an authenticated WebSocket
 * client that resumes from a cursor, de-duplicates by event id and reconciles
 * missed events after a reconnect.
 */
export interface EventBus {
  emit<TName extends EventName>(
    name: TName,
    payload: EventPayloadMap[TName],
    origin?: MicaraHrEvent['origin'],
  ): MicaraHrEvent<TName>;
  on<TName extends EventName>(name: TName, handler: EventHandler<TName>): Unsubscribe;
  onAny(handler: AnyEventHandler): Unsubscribe;
  /** Most recent events, newest first. Used by the Simulation Centre. */
  recent(limit?: number): AnyMicaraHrEvent[];
  clear(): void;
}

export interface MockEventBusOptions {
  /** Injected so tests and the app control time; defaults to `Date.now`. */
  now?: () => Date;
  /** How many events to retain for the Simulation Centre feed. */
  historyLimit?: number;
  idFactory?: () => string;
}

export function createMockEventBus(options: MockEventBusOptions = {}): EventBus {
  const now = options.now ?? (() => new Date());
  const historyLimit = options.historyLimit ?? 100;

  let counter = 0;
  const idFactory =
    options.idFactory ??
    (() => {
      counter += 1;
      return `evt_${counter.toString(36).padStart(6, '0')}`;
    });

  const handlers = new Map<EventName, Set<AnyEventHandler>>();
  const anyHandlers = new Set<AnyEventHandler>();
  const history: AnyMicaraHrEvent[] = [];

  return {
    emit(name, payload, origin = 'mock-service') {
      const event = {
        id: idFactory(),
        name,
        payload,
        emittedAt: now().toISOString(),
        origin,
      } as MicaraHrEvent<typeof name>;

      history.unshift(event as AnyMicaraHrEvent);
      if (history.length > historyLimit) history.length = historyLimit;

      const named = handlers.get(name);
      if (named) {
        for (const handler of [...named]) {
          handler(event as AnyMicaraHrEvent);
        }
      }
      for (const handler of [...anyHandlers]) {
        handler(event as AnyMicaraHrEvent);
      }

      return event;
    },

    on(name, handler) {
      const set = handlers.get(name) ?? new Set<AnyEventHandler>();
      set.add(handler as AnyEventHandler);
      handlers.set(name, set);
      return () => {
        set.delete(handler as AnyEventHandler);
      };
    },

    onAny(handler) {
      anyHandlers.add(handler);
      return () => {
        anyHandlers.delete(handler);
      };
    },

    recent(limit = 25) {
      return history.slice(0, limit);
    },

    clear() {
      history.length = 0;
    },
  };
}
